/**
 * @file CloseSiteFields.tsx
 * @description Поля окна «Закрыть сайт для покупателей» (phase19.16, SB138,
 * SB57; uiux-site-closed.md §3): текст для покупателей до 200 знаков со
 * счётчиком и проверкой цифр скидок до отправки, «Откроем примерно в»
 * (время магазина «чч:мм» + день, необязательно — только показ покупателю),
 * «Открыть автоматически» (через 1 ч | через 3 ч | без срока) с подписью
 * «Откроется сам сегодня в 15:20», подсказка про черновик конструктора.
 * Контракт `value` / `onChange` — как у полей перевозчика; проверки —
 * `close-site-model.ts`.
 */

import { SITE_CLOSURE_AUTO_OPEN_OPTIONS, SITE_CLOSURE_DEFAULT_MESSAGE, SITE_CLOSURE_MESSAGE_MAX, siteClosureDiscountTerm } from "@chaiberi/shared";
import { useId } from "react";

import { DateField } from "../ui/DateField";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { NoticeStrip } from "../ui/NoticeStrip";
import { Segmented } from "../site-view/Segmented";
import { TextField } from "../site-view/TextField";
import {
  AUTO_OPEN_LABELS,
  BUILDER_DRAFT_HINT,
  NO_DEADLINE_WARNING,
  autoOpenPreview,
  expectedDayWord,
  maskClockInput,
  type CloseSiteErrors,
  type CloseSiteForm,
} from "./close-site-model";

interface Props {
  value: CloseSiteForm;
  onChange: (patch: Partial<CloseSiteForm>) => void;
  errors: CloseSiteErrors;
  /** «Сейчас» по часам сервера. */
  now: Date;
  /** Пояс магазина. */
  zone: string;
  disabled?: boolean;
}

export function CloseSiteFields({ value, onChange, errors, now, zone, disabled }: Props) {
  const id = useId();
  // Цифры скидки видны сразу при вводе (SB57), а не только после «Закрыть сайт».
  const discount = siteClosureDiscountTerm(value.message);
  const messageError = errors.message ?? (discount ? `Размер скидки в этом тексте указывать нельзя — уберите «${discount}».` : null);
  const preview = autoOpenPreview(value.autoOpen, now, zone);
  const day = expectedDayWord(value, now, zone);
  // Ошибка времени читается вместе с полями времени и дня (а11y).
  const expectedDescribedBy = errors.expected ? `${id}-expected-hint ${id}-expected-error` : `${id}-expected-hint`;

  return (
    <div className="flex flex-col gap-5">
      <TextField
        label="Текст для покупателей"
        value={value.message}
        onChange={(message) => onChange({ message })}
        max={SITE_CLOSURE_MESSAGE_MAX}
        placeholder={SITE_CLOSURE_DEFAULT_MESSAGE}
        hint="Пусто — покажем текст из подсказки в поле."
        error={messageError}
        multiline
        disabled={disabled}
      />

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${id}-time`}>Откроем примерно в (необязательно)</Label>
        <div className="flex items-center gap-2">
          <Input
            id={`${id}-time`}
            inputMode="numeric"
            autoComplete="off"
            placeholder="17:00"
            className="w-24 shrink-0 tabular-nums"
            value={value.expectedTime}
            onChange={(e) => onChange({ expectedTime: maskClockInput(e.target.value) })}
            aria-invalid={!!errors.expected}
            aria-describedby={expectedDescribedBy}
            disabled={disabled}
          />
          <DateField
            value={value.expectedDay}
            onChange={(expectedDay) => onChange({ expectedDay })}
            ariaLabel="День, когда откроемся"
            title="День, когда откроемся"
            clearable={false}
            invalid={!!errors.expected}
            describedBy={expectedDescribedBy}
            disabled={disabled}
            className="min-w-0 flex-1"
          />
        </div>
        <p id={`${id}-expected-hint`} className="type-caption text-on-surface-variant">
          {value.expectedTime ? `Покупатели увидят: «Откроемся примерно в ${value.expectedTime}» — ${day}.` : "Только подсказка покупателям, сайт сам в это время не откроется."}
        </p>
        {errors.expected ? (
          <p id={`${id}-expected-error`} role="alert" className="type-caption text-destructive">
            {errors.expected}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-on-surface">Открыть автоматически</span>
        <Segmented
          aria-label="Открыть автоматически"
          options={SITE_CLOSURE_AUTO_OPEN_OPTIONS.map((option) => ({ value: option, label: AUTO_OPEN_LABELS[option], disabled }))}
          value={value.autoOpen}
          onChange={(autoOpen) => onChange({ autoOpen })}
        />
        {preview ? <p className="type-body text-on-surface">{preview}</p> : <NoticeStrip tone="warning">{NO_DEADLINE_WARNING}</NoticeStrip>}
      </div>

      <NoticeStrip tone="info">{BUILDER_DRAFT_HINT}</NoticeStrip>
    </div>
  );
}
